// Thin wrapper around `runOnboardingFlow` that owns two policy
// decisions specific to vault:
//
//   1. **404 short-circuit.** A null status fetch means the gateway has
//      ONBOARDING_ENABLED=false (production). We skip the orchestrator
//      entirely — the SDK's `ensure({ onboard: false })` will still
//      re-check gateway_is_proxy and throw if onboarding turns out to
//      be required.
//
//   2. **Initial-status cache.** The wrapper probes status once before
//      handing control to the orchestrator, then feeds that initial
//      value into the orchestrator's first iteration instead of
//      re-fetching. Saves a round-trip on every claim attempt.
//
//   3. **Mid-flow flip to disabled.** If a later status fetch returns
//      null (gateway config push between probes), we translate it to
//      a terminal `next_user_action: 'none'` rather than crashing the
//      claim mid-flight.
//
// Lifted out of `claim.ts` so each branch is unit-testable with
// injected dependencies; the wire-bound helpers (HTTP, autoAddProxy)
// stay in claim.ts.

import {
  runOnboardingFlow,
  type OnboardingFlowEvent,
  type OnboardingStatusWithAction,
} from './onboarding';

export interface RunVaultOnboardingDeps {
  /** GET /auth/onboarding/status — null when the gateway 404s (disabled). */
  getStatus: () => Promise<OnboardingStatusWithAction | null>;
  /** POST /auth/onboarding/bootstrap. */
  bootstrap: () => Promise<void>;
  /** Submit proxy.addProxy(<gateway>) via the user's wallet. */
  addProxy: () => Promise<{ txHash: string }>;
  onProgress?: (event: OnboardingFlowEvent) => void;
}

export async function runVaultOnboarding(deps: RunVaultOnboardingDeps): Promise<void> {
  const initial = await deps.getStatus();
  if (initial === null) return;

  let first = true;
  await runOnboardingFlow({
    getStatus: async () => {
      if (first) {
        first = false;
        return initial;
      }
      const next = await deps.getStatus();
      if (next === null) {
        return { ...initial, next_user_action: 'none' };
      }
      return next;
    },
    bootstrap: deps.bootstrap,
    addProxy: deps.addProxy,
    ...(deps.onProgress ? { onProgress: deps.onProgress } : {}),
  });
}
